<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type EstimatorReportComparisonDto, type EstimatorReportDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import EstimatorReportComparison from "@/views/comparison/EstimatorReportComparison.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();

const raiseError = inject(raiseErrorKey);

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);
const comparisonId = computed(() => parseInt(route.params.comparisonId as string));

const isLoading = ref(true);
const comparison = ref<EstimatorReportComparisonDto>();
const reports = ref<EstimatorReportDto[]>([]);
const selectedModelsIds = ref<number[]>([]);
const tradeoffExplanations = ref<{ reportId: string; explanation: string }[]>([]);

const breadcrumb = computed(() => {
  const last = comparison.value?.name ?? "Comparison";
  return [
    {
      label: workspacePublicId.value,
      to: { name: RouteNames.Projects, params: { workspacePublicId: workspacePublicId.value } },
      icon: "icon-masonry",
    },
    {
      label: projectName.value,
      to: {
        name: RouteNames.ProjectDetail,
        params: {
          workspacePublicId: workspacePublicId.value,
          projectName: projectName.value,
          section: ProjectDetailSections.Comparisons,
        },
      },
      icon: "icon-punch-card",
      iconColor: "var(--color-purple-500)",
    },
    {
      label: "Comparisons",
      to: {
        name: RouteNames.ProjectDetail,
        params: {
          workspacePublicId: workspacePublicId.value,
          projectName: projectName.value,
          section: ProjectDetailSections.Comparisons,
        },
      },
    },
    {
      label: last,
      icon: "icon-scale",
    },
  ];
});

async function loadComparison() {
  isLoading.value = true;

  const c = await api.fetchComparison(
    workspacePublicId.value,
    projectName.value,
    comparisonId.value
  );
  if (c === null) {
    raiseError?.(404);
  } else {
    comparison.value = c;

    // fetch reports and filter potentially not found
    const results = (
      await Promise.all(
        c.compared_reports.map((id) =>
          api.fetchEstimatorReport(workspacePublicId.value, projectName.value, id)
        )
      )
    ).filter((r) => r !== null);
    reports.value = results;

    // populate selected trade offs
    selectedModelsIds.value = c.best_trade_offs;
    tradeoffExplanations.value = Object.entries(c.best_trade_offs_explanations).map(
      ([reportId, explanation]) => ({
        reportId,
        explanation,
      })
    );
  }

  // render
  isLoading.value = false;
}

onMounted(async () => {
  await loadComparison();
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="breadcrumb">
    <template #default>
      <div class="comparison-view">
        <div class="back text-base-medium" @click="router.back()">
          <i class="icon icon-arrow-left" /> Comparisons
        </div>

        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>

        <div v-else class="comparison">
          <EstimatorReportComparison
            :reports="reports"
            :is-read-only="true"
            v-model:tradeoff-explanations="tradeoffExplanations"
            v-bind:selected-models-ids="selectedModelsIds"
          />
        </div>
      </div>
    </template>
  </DefaultPageLayout>
</template>

<style scoped>
.loading {
  display: flex;
  height: var(--page-remaining-height);
  align-items: center;
  justify-content: center;
}

.comparison-view {
  display: flex;
  height: 100%;
  flex-direction: column;
  gap: var(--numbers-16);

  .back {
    margin-top: var(--numbers-32);
    color: var(--content-secondary);
    cursor: pointer;

    .icon {
      margin-right: var(--numbers-8);
      vertical-align: middle;
    }
  }

  .loading {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  .comparison {
    overflow: hidden;
    height: 100%;
    flex-grow: 1;
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--numbers-6);

      .icon {
        margin-right: var(--numbers-6);
        color: var(--color-icon-tertiary);
        vertical-align: middle;
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-8);

      & > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--numbers-12);

        .name {
          color: var(--color-text-secondary);
          font-weight: var(--font-weight-medium);
        }

        &.copy-id {
          cursor: pointer;

          i {
            padding: var(--numbers-4);
            border: solid var(--color-stroke-background-primary) var(--stroke-width-m);
            border-radius: var(--numbers-4);
            vertical-align: middle;
          }
        }
      }
    }
  }
}
</style>
